import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { AtiraThunk } from "../AtiraThunk";
import { userActions } from "../user/user.slice";
import { ModuleDefinition } from "../../model/module/types/module-definition/ModuleDefinition";
import { GetUserModuleDefinitionsDto } from "../../model/module/dto/module-definition/GetUserModuleDefinitionsDto";
import { ModuleAxios } from "../../axios/module/module.axios";
import { CreateModuleDefinitionDto } from "../../model/module/dto/module-definition/CreateModuleDefinitionDto";
import { UpdateModuleDefinitionDto } from "../../model/module/dto/module-definition/UpdateModuleDefinitionDto";
import { CreateModuleInstanceDto } from "../../model/module/dto/module-instance/CreateModuleInstanceDto";
import { UpdateModuleInstanceDto } from "../../model/module/dto/module-instance/UpdateModuleInstanceDto";
import { PageDto } from "../../model/shared/meta/PageDto";
import { BaseModuleInstance } from "../../model/module/types/module-instance/BaseModuleInstance";
import { PageResponseDto } from "../../model/shared/meta/PageResponseDto";
import { GetModuleInstancesByDefinitionIdDto } from "../../model/module/dto/module-definition/GetModuleInstancesByDefinitionIdDto";
import { DeleteModuleDefinitionDto } from "../../model/module/dto/module-definition/DeleteModuleDefinitionDto";
import { DeleteModuleInstanceDto } from "../../model/module/dto/module-instance/DeleteModuleInstanceDto";
import { DeleteModuleInstanceAttachmentDto } from "../../model/module/dto/module-instance-attachment/DeleteModuleInstanceAttachmentDto";
import { CreateModuleInstanceAttachmentDto } from "../../model/module/dto/module-instance-attachment/CreateModuleInstanceAttachmentDto";
import { UpdateModuleInstanceAttachmentDto } from "../../model/module/dto/module-instance-attachment/UpdateModuleInstanceAttachmentDto";
import { ModuleDefinitionSetPermissionsDto } from "../../model/module/dto/module-definition/ModuleDefinitionSetPermissionsDto";
import { revertAllReduxAction } from "../store-reset";
import { GetModuleInstanceByIdDto } from "../../model/module/dto/module-instance/GetModuleInstanceByIdDto";

interface ModuleReducer {
  moduleDefinitions?: ModuleDefinition[];
  moduleDefinitionsLoading?: boolean;
  selectedModuleDefinition?: ModuleDefinition | null;
  allInstancesMeta: PageDto | null;
  instancesPage: number;
  instancesPageSize: number;
  instancesLoading?: boolean;
  instancesPendingRequestId?: string;
  instances?: BaseModuleInstance[];
}

const initialState = Object.freeze<ModuleReducer>({
  moduleDefinitions: [],
  moduleDefinitionsLoading: false,
  selectedModuleDefinition: null,
  allInstancesMeta: null,
  instancesPage: 1,
  instancesPageSize: 10,
  instancesLoading: false,
  instancesPendingRequestId: undefined,
  instances: [],
});

const getAllModuleDefinitions = AtiraThunk<
  ModuleDefinition[],
  GetUserModuleDefinitionsDto
>("/module/definitions", ModuleAxios.getAllModuleDefinitions);

const createModuleDefinition = AtiraThunk<void, CreateModuleDefinitionDto>(
  "/module-definition/create",
  ModuleAxios.createModuleDefinition,
);

const updateModuleDefinition = AtiraThunk<void, UpdateModuleDefinitionDto>(
  "/module-definition/update",
  ModuleAxios.updateModuleDefinition,
);

const createModuleInstance = AtiraThunk<void, CreateModuleInstanceDto>(
  "/module-instance/create",
  ModuleAxios.createModuleInstance,
);

const updateModuleInstance = AtiraThunk<void, UpdateModuleInstanceDto>(
  "/module-instance/update",
  ModuleAxios.updateModuleInstance,
);

const getModuleInstances = AtiraThunk<
  PageResponseDto<BaseModuleInstance[]>,
  GetModuleInstancesByDefinitionIdDto
>("/module-instance/all", ModuleAxios.getModuleInstances);

const getModuleInstanceById = AtiraThunk<
  BaseModuleInstance,
  GetModuleInstanceByIdDto
>("/module-instance/:id", ModuleAxios.getModuleInstanceById);

const deleteModuleDefinition = AtiraThunk<void, DeleteModuleDefinitionDto>(
  "/module-definition/delete",
  ModuleAxios.deleteModuleDefinition,
);

const deleteModuleInstance = AtiraThunk<void, DeleteModuleInstanceDto>(
  "/module-instance/delete",
  ModuleAxios.deleteModuleInstance,
);

const attachModuleInstanceToEntry = AtiraThunk<
  void,
  CreateModuleInstanceAttachmentDto
>("/module-instance/attach", ModuleAxios.attachModuleInstanceToEntry);

const updateModuleInstanceAttachment = AtiraThunk<
  void,
  UpdateModuleInstanceAttachmentDto
>(
  "/module-instance-attachment/:id",
  ModuleAxios.updateModuleInstanceAttachment,
);

const detachModuleInstanceFromEntry = AtiraThunk<
  void,
  DeleteModuleInstanceAttachmentDto
>("/module-instance/detach", ModuleAxios.detachModuleInstanceFromEntry);

const manageModuleDefinitionPermissions = AtiraThunk<
  void,
  ModuleDefinitionSetPermissionsDto
>(
  "/module-definition/:id/manage-assignees",
  ModuleAxios.manageModuleDefinitionPermissions,
);

const moduleSlice = createSlice({
  name: "module",
  initialState,
  reducers: {
    setSelectedModuleDefinition: (state, action) => {
      state.selectedModuleDefinition = action.payload;
    },

    setInstancesTablePageSize: (state, action) => {
      state.instancesPageSize = action.payload;
    },

    setInstancesTablePage: (state, action) => {
      state.instancesPage = action.payload;
    },
  },
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getAllModuleDefinitions.pending, (state, action) => {
      state.moduleDefinitionsLoading = true;
    });
    addCase(getAllModuleDefinitions.fulfilled, (state, action) => {
      state.moduleDefinitions = action.payload;
      state.moduleDefinitionsLoading = false;
    });
    addCase(getAllModuleDefinitions.rejected, (state, action) => {
      state.moduleDefinitionsLoading = false;
    });

    addCase(getModuleInstances.pending, (state, action) => {
      state.instancesPendingRequestId = action.meta.requestId;
      state.instancesLoading = true;
    });
    addCase(getModuleInstances.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.instancesPendingRequestId) {
        return;
      }

      state.allInstancesMeta = action.payload.meta;
      state.instances = action.payload.data;
      state.instancesLoading = false;
      state.instancesPendingRequestId = undefined;
    });
    addCase(getModuleInstances.rejected, (state, action) => {
      if (action.meta.requestId !== state.instancesPendingRequestId) {
        return;
      }

      state.instancesLoading = false;
      state.instancesPendingRequestId = undefined;
    });

    addCase(getModuleInstanceById.fulfilled, (state, action) => {
      const instanceIndex = state.instances?.findIndex(
        (inst) => inst._id === action.payload._id,
      );
      if (instanceIndex !== -1 && instanceIndex !== undefined) {
        state.instances![instanceIndex] = action.payload;
      } else {
        state.instances = state.instances
          ? [...state.instances, action.payload]
          : [action.payload];
      }
    });

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const moduleActions = {
  ...moduleSlice.actions,
  getAllModuleDefinitions,
  createModuleDefinition,
  updateModuleDefinition,
  createModuleInstance,
  updateModuleInstance,
  getModuleInstances,
  deleteModuleDefinition,
  deleteModuleInstance,
  attachModuleInstanceToEntry,
  updateModuleInstanceAttachment,
  detachModuleInstanceFromEntry,
  manageModuleDefinitionPermissions,
  getModuleInstanceById,
};

export default moduleSlice.reducer;
